import { useEffect, useMemo, useRef } from "react";
import {
  createChart,
  IChartApi,
  ISeriesApi,
  Time,
  CrosshairMode,
  ColorType,
  LineStyle,
  CandlestickSeries,
  LineSeries,
  HistogramSeries,
} from "lightweight-charts";
import { useGetOhlcvQuery } from "@/store/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";

interface Props {
  symbol: string;
  timeframe?: string;
  limit?: number;
  /** Optional override for the price-pane height in px. */
  heightPx?: number;
}

// ---------- indicator math (kept here so the chart is self-contained) ------

function ema(values: number[], period: number): number[] {
  if (values.length === 0) return [];
  const k = 2 / (period + 1);
  const out: number[] = [];
  let prev = values[0];
  out.push(prev);
  for (let i = 1; i < values.length; i++) {
    prev = values[i] * k + prev * (1 - k);
    out.push(prev);
  }
  return out;
}

function rsi(closes: number[], period = 14): number[] {
  const out: number[] = new Array(closes.length).fill(NaN);
  if (closes.length < period + 1) return out;
  let gain = 0;
  let loss = 0;
  for (let i = 1; i <= period; i++) {
    const ch = closes[i] - closes[i - 1];
    if (ch >= 0) gain += ch;
    else loss -= ch;
  }
  let avgGain = gain / period;
  let avgLoss = loss / period;
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < closes.length; i++) {
    const ch = closes[i] - closes[i - 1];
    const g = Math.max(ch, 0);
    const l = Math.max(-ch, 0);
    avgGain = (avgGain * (period - 1) + g) / period;
    avgLoss = (avgLoss * (period - 1) + l) / period;
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return out;
}

// ---------- component ------------------------------------------------------

/**
 * TradingView lightweight-charts: candles + EMA50/200 overlay on the main
 * pane, RSI(14) histogram below. Polls /api/ohlcv every 30s for the active
 * symbol/timeframe.
 */
export function PriceChart({ symbol, timeframe = "1m", limit = 240, heightPx = 360 }: Props) {
  const { data, isLoading, error, isFetching } = useGetOhlcvQuery(
    { symbol, timeframe, limit },
    { pollingInterval: 30_000 },
  );

  // --- transform raw rows into series-ready arrays -------------------------
  const series = useMemo(() => {
    const rows = data?.rows ?? [];
    if (rows.length === 0) {
      return null;
    }
    const candles = rows.map((r) => ({
      time: Math.floor(Number(r[0]) / 1000) as Time,
      open: Number(r[1]),
      high: Number(r[2]),
      low: Number(r[3]),
      close: Number(r[4]),
    }));
    const closes = rows.map((r) => Number(r[4]));
    const ema50 = ema(closes, 50);
    const ema200 = ema(closes, 200);
    const rsiVals = rsi(closes, 14);
    const ema50Series = candles.map((c, i) => ({ time: c.time, value: ema50[i] }));
    const ema200Series = candles.map((c, i) => ({ time: c.time, value: ema200[i] }));
    // RSI is undefined for the first `period` bars — skip them so the line
    // doesn't draw a wild jump from NaN.
    const rsiSeries = candles
      .map((c, i) => ({ time: c.time, value: rsiVals[i], color: rsiColor(rsiVals[i]) }))
      .filter((p) => Number.isFinite(p.value));
    return { candles, ema50Series, ema200Series, rsiSeries };
  }, [data]);

  // --- chart wiring --------------------------------------------------------
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const ema50Ref = useRef<ISeriesApi<"Line"> | null>(null);
  const ema200Ref = useRef<ISeriesApi<"Line"> | null>(null);
  const rsiSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const rsiContainerRef = useRef<HTMLDivElement>(null);
  const rsiChartRef = useRef<IChartApi | null>(null);

  // Build the charts once. We tear them down on unmount or when the symbol
  // changes (so axes auto-fit fresh data).
  useEffect(() => {
    if (!containerRef.current || !rsiContainerRef.current) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#9aa0a6",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.1)" },
      timeScale: { borderColor: "rgba(255,255,255,0.1)", timeVisible: true, secondsVisible: false },
      crosshair: { mode: CrosshairMode.Normal },
    });
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: "rgb(63, 195, 128)",
      downColor: "rgb(232, 65, 66)",
      borderUpColor: "rgb(63, 195, 128)",
      borderDownColor: "rgb(232, 65, 66)",
      wickUpColor: "rgb(63, 195, 128)",
      wickDownColor: "rgb(232, 65, 66)",
    });
    const ema50Series = chart.addSeries(LineSeries, {
      color: "#3b82f6",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });
    const ema200Series = chart.addSeries(LineSeries, {
      color: "#a855f7",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    const rsiChart = createChart(rsiContainerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#9aa0a6",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: {
        borderColor: "rgba(255,255,255,0.1)",
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderColor: "rgba(255,255,255,0.1)",
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: { mode: CrosshairMode.Normal },
    });
    const rsiSeries = rsiChart.addSeries(HistogramSeries, {
      base: 50,
      priceLineVisible: false,
    });
    rsiSeries.createPriceLine({
      price: 70,
      color: "rgba(232,65,66,0.4)",
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: "70",
    });
    rsiSeries.createPriceLine({
      price: 30,
      color: "rgba(63,195,128,0.4)",
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: "30",
    });

    // Sync the time axes so panning/zooming the price pane scrolls RSI too.
    let syncing = false;
    const sync = (range: import("lightweight-charts").LogicalRange | null) => {
      if (syncing || !range) return;
      syncing = true;
      rsiChart.timeScale().setVisibleLogicalRange(range);
      syncing = false;
    };
    const reverseSync = (range: import("lightweight-charts").LogicalRange | null) => {
      if (syncing || !range) return;
      syncing = true;
      chart.timeScale().setVisibleLogicalRange(range);
      syncing = false;
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(sync);
    rsiChart.timeScale().subscribeVisibleLogicalRangeChange(reverseSync);

    chartRef.current = chart;
    candleSeriesRef.current = candle;
    ema50Ref.current = ema50Series;
    ema200Ref.current = ema200Series;
    rsiChartRef.current = rsiChart;
    rsiSeriesRef.current = rsiSeries;

    return () => {
      // unsubscribe before remove() to avoid post-removal callbacks.
      try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(sync); } catch {/* ignore */}
      try { rsiChart.timeScale().unsubscribeVisibleLogicalRangeChange(reverseSync); } catch {/* ignore */}
      chart.remove();
      rsiChart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      ema50Ref.current = null;
      ema200Ref.current = null;
      rsiChartRef.current = null;
      rsiSeriesRef.current = null;
    };
  }, [symbol]);

  // Push new data into the existing series whenever it changes.
  useEffect(() => {
    if (!series) return;
    candleSeriesRef.current?.setData(series.candles);
    ema50Ref.current?.setData(series.ema50Series);
    ema200Ref.current?.setData(series.ema200Series);
    rsiSeriesRef.current?.setData(series.rsiSeries);
    chartRef.current?.timeScale().fitContent();
    rsiChartRef.current?.timeScale().fitContent();
  }, [series]);

  if (error) {
    return (
      <Alert tone="danger">
        Failed to load OHLCV for <code>{symbol}</code>. Make sure the pair is in your whitelist.
      </Alert>
    );
  }
  if (isLoading || !data) {
    return <Skeleton className="w-full" style={{ height: heightPx }} />;
  }
  if (data.rows.length === 0) {
    return (
      <div className="grid place-items-center text-muted text-sm" style={{ height: heightPx }}>
        No candles returned for {symbol} {timeframe}.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3 text-xs text-muted">
        <span>candles</span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-[#3b82f6]" /> EMA50
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-[#a855f7]" /> EMA200
        </span>
        <span className="ml-auto">{timeframe} · {data.rows.length} bars{isFetching && data ? " · refreshing" : ""}</span>
      </div>
      <div ref={containerRef} style={{ height: heightPx }} />
      <div className="text-xs text-muted">RSI(14)</div>
      <div ref={rsiContainerRef} style={{ height: 100 }} />
    </div>
  );
}

function rsiColor(v: number): string {
  if (!Number.isFinite(v)) return "rgba(150,150,150,0.4)";
  if (v >= 70) return "rgba(232,65,66,0.6)";
  if (v <= 30) return "rgba(63,195,128,0.6)";
  return "rgba(99,102,241,0.45)";
}
